import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingCart, Star } from 'lucide-react';
import { FigurineWithRating } from '@/lib/types';
import RatingStars from './RatingStars';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';

// Losowe kształty SVG placeholderów figurek
const PLACEHOLDER_SHAPES = [
  // humanoid warrior
  <svg key="1" viewBox="0 0 100 160" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="50" cy="18" rx="12" ry="13" fill="currentColor" opacity="0.7"/>
    <rect x="33" y="32" width="34" height="40" rx="6" fill="currentColor" opacity="0.6"/>
    <rect x="18" y="34" width="13" height="32" rx="5" fill="currentColor" opacity="0.5"/>
    <rect x="69" y="34" width="13" height="32" rx="5" fill="currentColor" opacity="0.5"/>
    <rect x="36" y="73" width="12" height="45" rx="5" fill="currentColor" opacity="0.6"/>
    <rect x="52" y="73" width="12" height="45" rx="5" fill="currentColor" opacity="0.6"/>
    <rect x="33" y="116" width="14" height="14" rx="3" fill="currentColor" opacity="0.5"/>
    <rect x="53" y="116" width="14" height="14" rx="3" fill="currentColor" opacity="0.5"/>
  </svg>,
  // dragon
  <svg key="2" viewBox="0 0 140 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="70" cy="70" rx="28" ry="20" fill="currentColor" opacity="0.6"/>
    <ellipse cx="70" cy="52" rx="16" ry="14" fill="currentColor" opacity="0.7"/>
    <ellipse cx="70" cy="38" rx="10" ry="9" fill="currentColor" opacity="0.75"/>
    <path d="M30 65 Q15 40 25 25 Q35 45 50 55" fill="currentColor" opacity="0.45"/>
    <path d="M110 65 Q125 40 115 25 Q105 45 90 55" fill="currentColor" opacity="0.45"/>
    <path d="M98 72 Q115 80 120 95 Q105 90 95 82" fill="currentColor" opacity="0.4"/>
    <path d="M42 72 Q25 80 20 95 Q35 90 45 82" fill="currentColor" opacity="0.4"/>
    <ellipse cx="45" cy="78" rx="8" ry="14" fill="currentColor" opacity="0.55"/>
    <ellipse cx="95" cy="78" rx="8" ry="14" fill="currentColor" opacity="0.55"/>
  </svg>,
  // mech robot
  <svg key="3" viewBox="0 0 100 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="32" y="5" width="36" height="30" rx="4" fill="currentColor" opacity="0.75"/>
    <rect x="26" y="37" width="48" height="48" rx="4" fill="currentColor" opacity="0.65"/>
    <rect x="10" y="40" width="14" height="38" rx="4" fill="currentColor" opacity="0.55"/>
    <rect x="76" y="40" width="14" height="38" rx="4" fill="currentColor" opacity="0.55"/>
    <rect x="8" y="75" width="18" height="10" rx="3" fill="currentColor" opacity="0.45"/>
    <rect x="74" y="75" width="18" height="10" rx="3" fill="currentColor" opacity="0.45"/>
    <rect x="34" y="86" width="14" height="44" rx="4" fill="currentColor" opacity="0.6"/>
    <rect x="52" y="86" width="14" height="44" rx="4" fill="currentColor" opacity="0.6"/>
    <rect x="30" y="128" width="18" height="12" rx="3" fill="currentColor" opacity="0.5"/>
    <rect x="52" y="128" width="18" height="12" rx="3" fill="currentColor" opacity="0.5"/>
  </svg>,
  // elf archer
  <svg key="4" viewBox="0 0 90 160" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="45" cy="16" rx="11" ry="12" fill="currentColor" opacity="0.72"/>
    <path d="M34 29 Q45 26 56 29 L62 72 Q45 78 28 72 Z" fill="currentColor" opacity="0.6"/>
    <path d="M28 35 Q16 42 14 58 Q22 54 30 50" fill="currentColor" opacity="0.5"/>
    <path d="M62 35 Q74 42 76 58 Q68 54 60 50" fill="currentColor" opacity="0.5"/>
    <rect x="36" y="72" width="10" height="48" rx="4" fill="currentColor" opacity="0.6"/>
    <rect x="50" y="72" width="10" height="48" rx="4" fill="currentColor" opacity="0.6"/>
    <path d="M5 45 L85 45" stroke="currentColor" strokeWidth="2.5" opacity="0.4" strokeLinecap="round"/>
    <path d="M5 45 L45 30 L85 45" stroke="currentColor" strokeWidth="2" opacity="0.35" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>,
  // wizard
  <svg key="5" viewBox="0 0 100 170" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 5 L62 35 L38 35 Z" fill="currentColor" opacity="0.65"/>
    <ellipse cx="50" cy="40" rx="14" ry="13" fill="currentColor" opacity="0.7"/>
    <path d="M25 55 Q50 50 75 55 L82 130 Q50 140 18 130 Z" fill="currentColor" opacity="0.55"/>
    <path d="M25 58 Q12 70 8 88 Q18 82 28 78" fill="currentColor" opacity="0.45"/>
    <path d="M75 58 Q88 70 92 88 Q82 82 72 78" fill="currentColor" opacity="0.45"/>
    <ellipse cx="50" cy="135" rx="22" ry="8" fill="currentColor" opacity="0.4"/>
    <circle cx="78" cy="76" r="8" fill="currentColor" opacity="0.35"/>
    <circle cx="82" cy="76" r="4" fill="currentColor" opacity="0.55"/>
  </svg>,
  // spaceship pilot
  <svg key="6" viewBox="0 0 100 160" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="50" cy="20" rx="16" ry="15" fill="currentColor" opacity="0.7"/>
    <ellipse cx="50" cy="20" rx="10" ry="10" fill="currentColor" opacity="0.45"/>
    <rect x="28" y="36" width="44" height="46" rx="8" fill="currentColor" opacity="0.62"/>
    <rect x="12" y="40" width="15" height="36" rx="6" fill="currentColor" opacity="0.5"/>
    <rect x="73" y="40" width="15" height="36" rx="6" fill="currentColor" opacity="0.5"/>
    <rect x="10" y="73" width="20" height="9" rx="3" fill="currentColor" opacity="0.4"/>
    <rect x="70" y="73" width="20" height="9" rx="3" fill="currentColor" opacity="0.4"/>
    <rect x="35" y="82" width="13" height="48" rx="5" fill="currentColor" opacity="0.58"/>
    <rect x="52" y="82" width="13" height="48" rx="5" fill="currentColor" opacity="0.58"/>
  </svg>,
  // creature
  <svg key="7" viewBox="0 0 120 140" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="60" cy="55" rx="30" ry="32" fill="currentColor" opacity="0.6"/>
    <ellipse cx="60" cy="28" rx="18" ry="16" fill="currentColor" opacity="0.7"/>
    <path d="M30 62 Q10 55 8 38 Q18 42 28 50" fill="currentColor" opacity="0.45"/>
    <path d="M90 62 Q110 55 112 38 Q102 42 92 50" fill="currentColor" opacity="0.45"/>
    <ellipse cx="42" cy="95" rx="10" ry="26" fill="currentColor" opacity="0.55"/>
    <ellipse cx="78" cy="95" rx="10" ry="26" fill="currentColor" opacity="0.55"/>
    <path d="M38 14 L48 28" stroke="currentColor" strokeWidth={4} strokeLinecap="round" opacity="0.5"/>
    <path d="M82 14 L72 28" stroke="currentColor" strokeWidth={4} strokeLinecap="round" opacity="0.5"/>
  </svg>,
  // knight
  <svg key="8" viewBox="0 0 100 165" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="34" y="4" width="32" height="28" rx="5" fill="currentColor" opacity="0.72"/>
    <rect x="36" y="8" width="28" height="20" rx="4" fill="currentColor" opacity="0.4"/>
    <rect x="28" y="33" width="44" height="46" rx="5" fill="currentColor" opacity="0.65"/>
    <rect x="12" y="36" width="15" height="36" rx="5" fill="currentColor" opacity="0.52"/>
    <rect x="73" y="36" width="15" height="36" rx="5" fill="currentColor" opacity="0.52"/>
    <rect x="8" y="68" width="22" height="8" rx="3" fill="currentColor" opacity="0.42"/>
    <rect x="70" y="68" width="22" height="8" rx="3" fill="currentColor" opacity="0.42"/>
    <rect x="35" y="79" width="13" height="50" rx="4" fill="currentColor" opacity="0.62"/>
    <rect x="52" y="79" width="13" height="50" rx="4" fill="currentColor" opacity="0.62"/>
    <rect x="33" y="126" width="15" height="14" rx="3" fill="currentColor" opacity="0.5"/>
    <rect x="52" y="126" width="15" height="14" rx="3" fill="currentColor" opacity="0.5"/>
  </svg>,
];

const CATEGORY_COLORS: Record<string, string> = {
  'sci-fi': 'oklch(0.68 0.28 195)',
  'fantasy': 'oklch(0.65 0.32 280)',
  'filmy': 'oklch(0.65 0.26 150)',
  'bajki': 'oklch(0.72 0.24 45)',
  'personalizowane': 'oklch(0.62 0.28 15)',
  'wlasne': 'oklch(0.68 0.28 195)',
};

interface FigurineCardProps {
  figurine: FigurineWithRating;
  index: number;
  onAuthRequired: () => void;
}

export default function FigurineCard({ figurine, index, onAuthRequired }: FigurineCardProps) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const shapeIndex = index % PLACEHOLDER_SHAPES.length;
  const accentColor = CATEGORY_COLORS[figurine.category_slug ?? ''] ?? 'var(--primary)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.07, type: 'spring', stiffness: 280, damping: 30 }}
      whileHover={{ scale: 1.02, y: -4 }}
      className="group relative rounded-2xl border overflow-hidden cursor-pointer"
      style={{
        background: 'oklch(0.16 0.03 280)',
        borderColor: 'oklch(0.25 0.045 280)',
        boxShadow: '0 4px 24px -8px oklch(0.12 0.025 280)',
      }}
      onClick={() => navigate(`/figurine/${figurine.slug}`)}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = accentColor;
        (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 32px -8px ${accentColor}55`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'oklch(0.25 0.045 280)';
        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 24px -8px oklch(0.12 0.025 280)';
      }}
    >
      {/* Figurine visual area – 9:16 */}
      <div
        className="relative w-full flex items-center justify-center overflow-hidden"
        style={{
          aspectRatio: '9/16',
          background: `radial-gradient(ellipse 80% 80% at 50% 60%, ${accentColor}18 0%, oklch(0.13 0.028 280) 70%)`,
        }}
      >
        {/* Grid pattern */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(oklch(0.25 0.045 280 / 0.3) 1px, transparent 1px), linear-gradient(90deg, oklch(0.25 0.045 280 / 0.3) 1px, transparent 1px)`,
          backgroundSize: '20px 20px',
        }} />

        {/* 3D Shape placeholder OR real image */}
        {figurine.image_url ? (
          <div className="absolute inset-0">
            <img
              src={figurine.image_url}
              alt={figurine.title}
              className="w-full h-full object-cover"
              style={{ filter: `brightness(0.92) saturate(1.1)` }}
            />
            <div className="absolute inset-0" style={{
              background: `linear-gradient(to bottom, transparent 55%, oklch(0.13 0.028 280) 100%)`,
            }} />
          </div>
        ) : (
          <motion.div
            className="relative z-10 w-28 h-auto"
            style={{ color: accentColor, filter: `drop-shadow(0 0 12px ${accentColor}80)` }}
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4 + (index % 3), repeat: Infinity, ease: 'easeInOut' }}
          >
            {PLACEHOLDER_SHAPES[shapeIndex]}
          </motion.div>
        )}

        {/* Featured badge */}
        {figurine.is_featured && (
          <div
            className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
            style={{ fontFamily: 'JetBrains Mono, monospace', background: 'oklch(0.72 0.24 45 / 0.2)', color: 'var(--chart-4)', border: '1px solid oklch(0.72 0.24 45 / 0.4)' }}
          >
            <Star className="w-3 h-3 fill-current" />
            FEATURED
          </div>
        )}

        {/* Category badge */}
        <div
          className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold tracking-wide"
          style={{ fontFamily: 'Rajdhani, sans-serif', background: `${accentColor}22`, color: accentColor, border: `1px solid ${accentColor}55` }}
        >
          {figurine.category_slug?.toUpperCase() ?? 'INNE'}
        </div>

        {/* Hover overlay with Add to Cart */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          style={{ background: 'oklch(0.10 0.022 280 / 0.7)' }}
        >
          <Button
            size="sm"
            className="gap-2 font-bold"
            style={{ background: accentColor, color: 'white', boxShadow: `0 0 20px ${accentColor}60` }}
            onClick={(e) => { e.stopPropagation(); if (!user) { onAuthRequired(); return; } addItem(figurine.id); }}
          >
            <ShoppingCart className="w-4 h-4" />
            DO KOSZYKA
          </Button>
        </motion.div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3
          className="font-bold text-foreground text-base mb-1 truncate"
          style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '0.85rem', letterSpacing: '0.04em' }}
        >
          {figurine.title}
        </h3>
        {figurine.description && (
          <p className="text-muted-foreground text-sm mb-3 line-clamp-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
            {figurine.description}
          </p>
        )}

        <div className="flex items-center justify-between">
          <RatingStars
            figurineId={figurine.id}
            avgRating={figurine.avg_rating}
            ratingCount={figurine.rating_count}
            size="sm"
            onAuthRequired={onAuthRequired}
          />
          {figurine.price_pln != null && (
            <span
              className="font-black text-lg"
              style={{ fontFamily: 'JetBrains Mono, monospace', color: accentColor }}
            >
              {figurine.price_pln.toFixed(2)} zł
            </span>
          )}
        </div>

        {/* Tags */}
        {figurine.tags.length > 0 && (
          <div className="flex gap-1 mt-3 flex-wrap">
            {figurine.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: 'oklch(0.22 0.04 280)', color: 'var(--muted-foreground)', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
